import { z } from "zod";
/** Hidden honeypot + Cloudflare Turnstile token on public write endpoints. */
export const botProtectionFields = {
    /** Must stay empty — bots often fill hidden "website" inputs. */
    website: z.string().max(0).optional(),
    /** Verified server-side when `TURNSTILE_SECRET_KEY` is set. */
    turnstileToken: z.string().min(1).optional(),
};
export const subscribeSchema = z.object({
    email: z.string().email().max(254),
    name: z.string().min(1).max(120).optional(),
    ...botProtectionFields,
});
export const adminCreateSubscriberSchema = z.object({
    email: z.string().email().max(254),
    name: z.string().min(1).max(120).optional(),
});
export const adminUpdateSubscriberSchema = z.object({
    email: z.string().email().max(254).optional(),
    name: z.string().min(1).max(120).nullable().optional(),
});
export const subscriberBroadcastSchema = z.object({
    subject: z.string().min(1).max(200),
    body: z.string().min(1).max(50_000),
    /** When true, sends only to the logged-in admin's email (test). */
    testOnly: z.boolean().optional(),
});
export const rsvpSchema = z.object({
    name: z.string().min(1).max(120),
    email: z.string().email().max(254),
    phone: z.string().min(3).max(40).optional(),
    seats: z.number().int().min(1).max(20),
    ...botProtectionFields,
});
/** Door-list location written for public livestream self check-in. */
export const ONLINE_CHECK_IN_LOCATION = "Online";
export const publicCheckInSchema = z.object({
    name: z.string().min(1).max(120),
    email: z.string().email().max(254),
    phone: z.string().min(3).max(40).optional(),
    ...botProtectionFields,
});
export const partnerTierSchema = z.enum(["monthly", "one_night", "recording"]);
export const partnerSchema = z.object({
    tier: partnerTierSchema,
    name: z.string().min(1).max(120),
    email: z.string().email().max(254),
    amountUgx: z.number().int().min(1000).max(1_000_000_000),
    ...botProtectionFields,
});
export const registerSchema = z.object({
    name: z.string().min(1).max(120),
    email: z.string().email().max(254),
    password: z.string().min(8).max(200),
    ...botProtectionFields,
});
export const loginSchema = z.object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(200),
    ...botProtectionFields,
});
const testimonialPhotoUrlSchema = z
    .string()
    .max(500)
    .refine((s) => s.startsWith("/uploads/testimonials/pending/") && !s.includes(".."), {
    message: "Invalid photo URL",
});
export const testimonialSchema = z.object({
    name: z.string().min(1).max(120),
    location: z.string().min(1).max(120).optional(),
    body: z.string().min(10).max(600),
    photoUrls: z.array(testimonialPhotoUrlSchema).max(3).optional(),
    ...botProtectionFields,
});
export const liveCommentStatusSchema = z.enum(["PENDING", "PUBLISHED", "REJECTED"]);
export const liveCommentSchema = z.object({
    body: z.string().trim().min(1).max(1000),
});
export const moderateLiveCommentSchema = z.object({
    status: liveCommentStatusSchema,
});
export const streamSourceSchema = z.object({
    kind: z.enum(["hls", "mp4", "youtube"]),
    src: z.string().min(1).max(2000),
    poster: z.string().url().max(2000).nullable().optional(),
});
export const liveStreamRefSchema = z.object({
    source: streamSourceSchema.nullable().optional(),
    youtubeId: z.string().min(1).max(40).nullable().optional(),
    title: z.string().min(1).max(200),
    subtitle: z.string().max(400).nullable(),
});
export const liveInfoSchema = z.object({
    isLive: z.boolean(),
    current: liveStreamRefSchema
        .extend({ startedAt: z.string().datetime().nullable() })
        .nullable(),
    last: liveStreamRefSchema.extend({ streamedAt: z.string().datetime().nullable() }),
});
export const contactSchema = z.object({
    name: z.string().min(1).max(120),
    email: z.string().email().max(254),
    subject: z.string().min(1).max(160).optional(),
    body: z.string().min(5).max(2000),
    ...botProtectionFields,
});
export const blogStatusSchema = z.enum(["DRAFT", "PUBLISHED"]);
export const bodyFormatSchema = z.enum(["MARKDOWN", "BLOCKS"]);
/** Minimal sanity check for BlockNote document JSON stored in `body`. */
const blockNoteBlockSchema = z
    .object({
    id: z.string(),
    type: z.string(),
})
    .passthrough();
export const blockNoteBodySchema = z
    .string()
    .min(2)
    .max(200_000)
    .refine((s) => {
    try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) && parsed.every((b) => blockNoteBlockSchema.safeParse(b).success);
    }
    catch {
        return false;
    }
}, { message: "Body must be a JSON array of BlockNote blocks" });
/** Same rules as event poster: absolute path or http(s) URL (uploads use `/uploads/…`). */
const blogCoverUrlSchema = z
    .string()
    .min(1)
    .max(500)
    .refine((s) => s.startsWith("/") || /^https?:\/\//i.test(s), { message: "Must be a full URL or a path starting with /" });
export const blogPostSchema = z
    .object({
    title: z.string().min(1).max(200),
    slug: z.string().min(1).max(200).regex(/^[a-z0-9-]+$/i).optional(),
    excerpt: z.string().max(400).optional(),
    body: z.string().min(1),
    bodyFormat: bodyFormatSchema.default("MARKDOWN"),
    coverUrl: blogCoverUrlSchema.optional(),
    status: blogStatusSchema.default("DRAFT"),
    eventId: z.number().int().positive().nullable().optional(),
})
    .superRefine((data, ctx) => {
    if (data.bodyFormat === "BLOCKS") {
        const result = blockNoteBodySchema.safeParse(data.body);
        if (!result.success) {
            for (const issue of result.error.issues) {
                ctx.addIssue({ ...issue, path: ["body"] });
            }
        }
    }
    else if (data.body.length > 50_000) {
        ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum: 50_000,
            type: "string",
            inclusive: true,
            message: "Markdown body must be at most 50,000 characters",
            path: ["body"],
        });
    }
});
export const sitePageSchema = blogPostSchema;
export const requestStatusSchema = z.enum(["NEW", "IN_PROGRESS", "DONE"]);
export const adminCreatePartnerSchema = partnerSchema;
export const adminUpdatePartnerSchema = z.object({
    tier: partnerTierSchema.optional(),
    name: z.string().min(1).max(120).optional(),
    email: z.string().email().max(254).optional(),
    amountUgx: z.number().int().min(1000).max(1_000_000_000).optional(),
    status: requestStatusSchema.optional(),
    notes: z.string().max(2000).nullable().optional(),
});
export const updateRequestStatusSchema = z.object({
    status: requestStatusSchema,
    notes: z.string().max(2000).optional(),
});
export const moderateTestimonialSchema = z.object({
    status: z.enum(["PUBLISHED", "REJECTED"]),
});
export const adminCreateTestimonialSchema = z.object({
    name: z.string().min(1).max(120),
    location: z.string().min(1).max(120).optional(),
    body: z.string().min(10).max(600),
    photoUrls: z
        .array(z
        .string()
        .max(500)
        .refine((s) => s.startsWith("/uploads/") && !s.includes(".."), {
        message: "Photo URL must be an /uploads/ path",
    }))
        .max(3)
        .optional(),
    /** Defaults to published so admin-added stories appear on the site immediately. */
    status: z.enum(["PENDING", "PUBLISHED"]).default("PUBLISHED"),
});
const movementMilestoneYearSchema = z
    .string()
    .min(1)
    .max(32)
    .refine((value) => /^\d{4}$/.test(value) || /^\d{4}-\d{2}-\d{2}$/.test(value), {
    message: "Use a year (2019) or date (2019-03-15)",
});
const movementMilestoneLinkRefine = (data, ctx) => {
    if (data.blogPostId != null && data.galleryAlbumId != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Choose either a blog post or a photo album, not both",
            path: ["blogPostId"],
        });
    }
};
export const adminCreateMovementMilestoneSchema = z
    .object({
    year: movementMilestoneYearSchema,
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(4000),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    blogPostId: z.number().int().positive().nullable().optional(),
    galleryAlbumId: z.number().int().positive().nullable().optional(),
})
    .superRefine(movementMilestoneLinkRefine);
export const adminUpdateMovementMilestoneSchema = z
    .object({
    year: movementMilestoneYearSchema.optional(),
    title: z.string().min(1).max(200).optional(),
    body: z.string().min(1).max(4000).optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    blogPostId: z.number().int().positive().nullable().optional(),
    galleryAlbumId: z.number().int().positive().nullable().optional(),
})
    .superRefine(movementMilestoneLinkRefine);
export const updateContentSchema = z.object({
    valueJson: z.unknown(),
});
export const siteModeSchema = z.enum(["coming-soon", "live"]);
export const siteSettingsSchema = z.object({
    mode: siteModeSchema,
    launchAt: z.string().datetime(),
    announcementEnabled: z.boolean(),
    announcementTitle: z.string().min(1).max(120),
    announcementBody: z.string().min(1).max(2000),
});
export const updateSiteSettingsSchema = z.object({
    mode: siteModeSchema.optional(),
    launchAt: z.string().datetime().optional(),
    announcementEnabled: z.boolean().optional(),
    announcementTitle: z.string().min(1).max(120).optional(),
    announcementBody: z.string().min(1).max(2000).optional(),
});
export const footerSettingsSchema = z.object({
    copyright: z.string().min(1).max(300),
    verse: z.string().min(1).max(500),
});
export const updateFooterSettingsSchema = z.object({
    copyright: z.string().min(1).max(300).optional(),
    verse: z.string().min(1).max(500).optional(),
});
export const updateEmailSettingsSchema = z.object({
    host: z.string().min(1).max(253),
    port: z.number().int().min(1).max(65535),
    user: z.string().min(1).max(254),
    fromEmail: z.string().email().max(254),
    secure: z.boolean(),
    /** Omit or empty string keeps the existing encrypted password. */
    password: z.string().max(500).optional(),
});
export const updateYoutubeSettingsSchema = z.object({
    /** Omit or empty string keeps the existing encrypted API key. */
    apiKey: z.string().max(500).optional(),
    /** YouTube channel id (UC…) or @handle. Omit or empty clears stored channel. */
    channelId: z.string().max(200).optional(),
});
export const smsAudienceSchema = z.enum([
    "checked_in",
    "attendees",
    "reservations",
    "all_with_phones",
]);
export const updateSmsSettingsSchema = z.object({
    username: z.string().min(1).max(120),
    /** Approved Africa's Talking sender ID / shortcode. Empty keeps existing. */
    senderId: z.string().max(20).optional(),
    defaultCountryCode: z.string().regex(/^\d{1,4}$/, "Use digits only, e.g. 256"),
    sandbox: z.boolean(),
    /** Omit or empty string keeps the existing encrypted API key. */
    apiKey: z.string().max(500).optional(),
});
export const sendSmsSchema = z.object({
    eventId: z.number().int().positive(),
    audience: smsAudienceSchema,
    message: z.string().min(1).max(1000),
});
const contactTagSchema = z.string().trim().min(1).max(40);
export const adminCreateContactSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    phone: z.string().trim().min(7).max(40).optional().or(z.literal("")),
    email: z.string().email().max(254).optional().or(z.literal("")),
    tags: z.array(contactTagSchema).max(20).optional(),
    subscribed: z.boolean().optional(),
    notes: z.string().max(400).optional(),
})
    .refine((v) => Boolean((v.phone && v.phone.trim()) || (v.email && v.email.trim())), {
    message: "Add a mobile number or an email.",
});
export const adminUpdateContactSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    phone: z.string().trim().min(7).max(40).optional().or(z.literal("")),
    email: z.string().email().max(254).optional().or(z.literal("")),
    tags: z.array(contactTagSchema).max(20).optional(),
    subscribed: z.boolean().optional(),
    notes: z.string().max(400).optional(),
});
export const adminContactImportSourceSchema = z.enum([
    "attendees",
    "reservations",
    "all",
]);
export const adminContactSyncSourceSchema = z.enum([
    "subscribers",
    "users",
    "attendees",
    "all",
]);
export const adminSyncContactsSchema = z.object({
    source: adminContactSyncSourceSchema,
});
export const adminMergeContactsSchema = z.object({
    survivorId: z.number().int().positive(),
    mergeIds: z.array(z.number().int().positive()).min(1).max(50),
});
/** Pull mobile numbers already in the system into the address book. */
export const adminImportContactsSchema = z.object({
    source: adminContactImportSourceSchema,
    /** Limit the import to one event. Omit to sweep every event. */
    eventId: z.number().int().positive().optional(),
    /** Tags applied to every contact created by this import. */
    tags: z.array(contactTagSchema).max(20).optional(),
});
/** Paste-in bulk add: one contact per line, "Name, +2567..." or just a number. */
export const adminBulkContactsSchema = z.object({
    text: z.string().min(1).max(200_000),
    tags: z.array(contactTagSchema).max(20).optional(),
});
export const adminDeleteContactsSchema = z.object({
    ids: z.array(z.number().int().positive()).min(1).max(5000),
});
export const smsBroadcastTargetSchema = z.discriminatedUnion("kind", [
    /** Every subscribed contact in the address book. */
    z.object({ kind: z.literal("contacts") }),
    /** Subscribed contacts carrying at least one of these tags. */
    z.object({ kind: z.literal("tags"), tags: z.array(contactTagSchema).min(1).max(20) }),
    /** Explicitly picked contacts. */
    z.object({ kind: z.literal("selected"), ids: z.array(z.number().int().positive()).min(1).max(5000) }),
    /** An event audience, same groups the attendance screen offers. */
    z.object({ kind: z.literal("event"), eventId: z.number().int().positive(), audience: smsAudienceSchema }),
    /** Ad-hoc numbers typed into the compose box. */
    z.object({ kind: z.literal("manual"), numbers: z.array(z.string().min(7).max(40)).min(1).max(1000) }),
]);
export const sendSmsBroadcastSchema = z.object({
    target: smsBroadcastTargetSchema,
    message: z.string().min(1).max(1000),
});
export const testSmsSchema = z.object({
    to: z.string().min(7).max(40),
    message: z.string().min(1).max(480).optional(),
});
/** Record a prepaid SMS credit after paying via Airtel Money / provider. */
export const smsTopUpSchema = z.object({
    amountUgx: z.number().int().positive().max(50_000_000),
    note: z.string().trim().max(400).optional(),
});
export const emailAttendanceReportSchema = z.object({
    /** Extra recipients. The signed-in admin is always included. */
    to: z.array(z.string().email().max(254)).max(10).optional(),
});
export const adminFeatureIdSchema = z.enum([
    "dashboard",
    "check_in",
    "attendance",
    "events",
    "rsvps",
    "content",
    "inbox",
    "settings",
    "sms",
    "users",
]);
export const adminCreateUserSchema = z.object({
    name: z.string().min(1).max(120),
    email: z.string().email().max(254),
    password: z.string().min(8).max(200),
    role: z.enum(["USER", "ADMIN"]),
    /** Empty = full admin access. Ignored for USER. */
    permissions: z.array(adminFeatureIdSchema).max(20).optional(),
});
export const adminUpdateUserSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    email: z.string().email().max(254).optional(),
    role: z.enum(["USER", "ADMIN"]).optional(),
    permissions: z.array(adminFeatureIdSchema).max(20).optional(),
});
export const adminResetPasswordSchema = z.object({
    password: z.string().min(8).max(200),
});
const posterUrlSchema = z
    .string()
    .min(1)
    .max(500)
    .refine((s) => s.startsWith("/") || /^https?:\/\//i.test(s), { message: "Must be a full URL or a path starting with /" });
const hexColorSchema = z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Must be a hex color like #F25C1F");
export const eventThemeSchema = z.object({
    primary: hexColorSchema,
    highlight: hexColorSchema,
    deep: hexColorSchema,
    background: hexColorSchema.optional(),
    usePrimary: z.boolean(),
    useHighlight: z.boolean(),
    useDeep: z.boolean(),
    useBackground: z.boolean().optional(),
    applyWhenLive: z.boolean(),
});
export const eventStreamConfigSchema = z.object({
    source: streamSourceSchema.nullable().optional(),
    youtubeId: z.string().min(1).max(40).nullable().optional(),
    subtitle: z.string().max(400).nullable().optional(),
});
export const eventStatusSchema = z.enum(["SCHEDULED", "CANCELLED"]);
export const createScheduledEventSchema = z.object({
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime().optional(),
    posterUrl: posterUrlSchema.optional(),
    title: z.string().max(200).optional(),
    status: eventStatusSchema.optional(),
    publicMessage: z.string().max(2000).nullable().optional(),
    detailsJson: z.unknown().optional(),
    themeJson: eventThemeSchema.optional(),
    streamJson: eventStreamConfigSchema.nullable().optional(),
});
export const updateScheduledEventSchema = z.object({
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().optional(),
    posterUrl: posterUrlSchema.optional(),
    title: z.string().max(200).nullable().optional(),
    status: eventStatusSchema.optional(),
    publicMessage: z.string().max(2000).nullable().optional(),
    detailsJson: z.unknown().nullable().optional(),
    themeJson: eventThemeSchema.nullable().optional(),
    streamJson: eventStreamConfigSchema.nullable().optional(),
});
export const photoCommentStatusSchema = z.enum(["PENDING", "PUBLISHED", "REJECTED"]);
export const photoCommentSchema = z.object({
    body: z.string().trim().min(1).max(1000),
});
export const moderatePhotoCommentSchema = z.object({
    status: photoCommentStatusSchema,
});
export const createGalleryAlbumSchema = z.object({
    title: z.string().min(1).max(200),
    caption: z.string().max(400).nullable().optional(),
    eventId: z.number().int().positive().nullable().optional(),
    coverUrl: z.string().max(500).nullable().optional(),
    published: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const updateGalleryAlbumSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    slug: z.string().min(1).max(200).regex(/^[a-z0-9-]+$/i).optional(),
    caption: z.string().max(400).nullable().optional(),
    eventId: z.number().int().positive().nullable().optional(),
    coverUrl: z.string().max(500).nullable().optional(),
    published: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const createGalleryPhotoSchema = z.object({
    url: z.string().min(1).max(500),
    caption: z.string().max(400).nullable().optional(),
    sortOrder: z.number().int().optional(),
    width: z.number().int().positive().nullable().optional(),
    height: z.number().int().positive().nullable().optional(),
});
export const updateGalleryPhotoSchema = z.object({
    caption: z.string().max(400).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export const reorderGalleryPhotosSchema = z.object({
    photoIds: z.array(z.number().int().positive()).min(1),
});
const siteContactHrefSchema = z
    .string()
    .min(1)
    .max(500)
    .refine((s) => /^(https?:\/\/|mailto:|tel:)/i.test(s), { message: "Must start with http://, https://, mailto:, or tel:" });
export const adminCreateSiteContactLinkSchema = z.object({
    label: z.string().min(1).max(120),
    href: siteContactHrefSchema,
    sortOrder: z.number().int().min(0).max(9999).optional(),
    published: z.boolean().optional(),
});
export const adminUpdateSiteContactLinkSchema = z.object({
    label: z.string().min(1).max(120).optional(),
    href: siteContactHrefSchema.optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    published: z.boolean().optional(),
});
export const reorderSiteContactLinksSchema = z.object({
    linkIds: z.array(z.number().int().positive()).min(1),
});
const siteSocialHrefSchema = z
    .string()
    .min(1)
    .max(500)
    .refine((s) => /^https?:\/\//i.test(s), { message: "Must start with http:// or https://" });
export const adminCreateSiteSocialLinkSchema = z.object({
    label: z.string().min(1).max(120),
    href: siteSocialHrefSchema,
    sortOrder: z.number().int().min(0).max(9999).optional(),
    published: z.boolean().optional(),
});
export const adminUpdateSiteSocialLinkSchema = z.object({
    label: z.string().min(1).max(120).optional(),
    href: siteSocialHrefSchema.optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    published: z.boolean().optional(),
});
export const reorderSiteSocialLinksSchema = z.object({
    linkIds: z.array(z.number().int().positive()).min(1),
});
const attendeeFieldsSchema = z.object({
    name: z.string().trim().min(1).max(120),
    mobile: z.string().trim().max(40).nullable().optional(),
    location: z.string().trim().max(120).nullable().optional(),
    email: z
        .string()
        .trim()
        .max(254)
        .nullable()
        .optional()
        .refine((v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), "Invalid email"),
    notes: z.string().trim().max(400).nullable().optional(),
});
export const createEventAttendeeSchema = attendeeFieldsSchema.extend({
    checkIn: z.boolean().optional(),
});
export const updateEventAttendeeSchema = attendeeFieldsSchema.partial();
export const bulkCreateEventAttendeesSchema = z.object({
    attendees: z.array(attendeeFieldsSchema).min(1).max(500),
    checkInAll: z.boolean().optional(),
});
export const participationPromptKindSchema = z.enum([
    "OPEN_TEXT",
    "YES_NO",
    "TESTIMONIAL",
    "PASTORAL",
    "ACTION_RSVP",
    "ACTION_INVITE",
    "ACTION_PREP",
]);
export const participationResponseStatusSchema = z.enum([
    "PENDING",
    "PUBLISHED",
    "REJECTED",
    "PRIVATE",
]);
export const participationOpenTextSchema = z.object({
    promptSlug: z.string().min(1).max(80),
    body: z.string().trim().min(1).max(600),
    name: z.string().trim().max(120).optional(),
    ...botProtectionFields,
});
export const participationYesNoSchema = z.object({
    promptSlug: z.string().min(1).max(80),
    yesNo: z.boolean(),
    ...botProtectionFields,
});
export const participationPastoralSchema = z.object({
    promptSlug: z.string().min(1).max(80),
    name: z.string().trim().min(1).max(120),
    phone: z.string().trim().min(7).max(40),
    body: z.string().trim().max(500).optional(),
    ...botProtectionFields,
});
export const participationAggregateTapSchema = z.object({
    promptSlug: z.string().min(1).max(80),
    yesNo: z.boolean().optional(),
    ...botProtectionFields,
});
export const moderateParticipationResponseSchema = z.object({
    status: participationResponseStatusSchema,
});
const prepChecklistItemSchema = z.object({
    id: z.string().min(1).max(40),
    label: z.string().min(1).max(200),
    href: z.string().max(200).optional(),
});
export const participationScheduleSchema = z.object({
    timezone: z.literal("Africa/Kampala"),
    weeklySpotlight: z.record(z.enum(["0", "1", "2", "3", "4", "5", "6"]), z.string().min(1).max(80)),
    dateOverrides: z.record(z.string(), z.string().min(1).max(80)),
    eventProximity: z.record(z.string(), z.string().min(1).max(80)),
    prepChecklist: z.array(prepChecklistItemSchema),
    announceDays: z.number().int().min(1).max(90),
    approachDays: z.number().int().min(1).max(60),
});
export const adminUpdateParticipationPromptSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    subtitle: z.string().max(400).nullable().optional(),
    placeholder: z.string().max(200).nullable().optional(),
    dayLabel: z.string().max(40).nullable().optional(),
    enabled: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(0).max(600).optional(),
});
export const updateYoutubeVideoSiteMetaSchema = z.object({
    category: z.string().max(80).nullable().optional(),
    description: z.string().max(10000).nullable().optional(),
    tags: z.array(z.string().max(60)).max(30).optional(),
    eventId: z.number().int().positive().nullable().optional(),
    publishedOnSite: z.boolean().optional(),
});
//# sourceMappingURL=schemas.js.map